import { ReactNode } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/material'
import { theme } from '../../theme/theme'
import { ROUTES } from '../../constants/routes'
import { ComponentLayout } from '../layouts/ComponentLayout'
import { ScreeningHeader } from '../screening/ScreeningHeader'
import { BodyLarge, Title4 } from '../styles/Typography'
import { ErrorSign } from '../icons'
import { useWindowWidth } from '../../hooks/useWindowWidth'
import { isTabletOrMobile } from '../../utils/devices'

type Props = {
  title: string
  goBack: () => void
  errorMessage?: string | null
  children: ReactNode
  primaryAction: ReactNode
  secondaryAction?: ReactNode
}

export const CertificationStepLayout = ({
  title,
  goBack,
  errorMessage,
  children,
  primaryAction,
  secondaryAction,
}: Props) => {
  const { t } = useTranslation()
  const width = useWindowWidth()

  return (
    <>
      <ScreeningHeader
        title={t('admin.certification.pageTitle')}
        goBack={goBack}
        progress={100}
        closePath={ROUTES.ADMIN_CERTIFICATION}
      />
      <ComponentLayout
        noMaxWidth
        paddingTop={isTabletOrMobile(width) ? theme.spacing.xsmall : theme.spacing.xxlarge}
        style={{ height: '100%' }}
      >
        <Section gap={10}>
          <div>
            <StepTitle>{title}</StepTitle>
            {children}
          </div>
          <div>
            {errorMessage && (
              <ErrorContainer>
                <ErrorSign size={24} color={theme.color.red} />
                <BodyLarge>{errorMessage}</BodyLarge>
              </ErrorContainer>
            )}
            <Actions>
              <PrimaryAction>{primaryAction}</PrimaryAction>
              {secondaryAction}
            </Actions>
          </div>
        </Section>
      </ComponentLayout>
    </>
  )
}

const Section = styled(Stack)`
  max-width: 600px;
  margin: 0 auto;
  text-align: left;
  height: 100%;
`

const StepTitle = styled(Title4)`
  margin-bottom: ${theme.spacing.medium}px;
`

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xsmall}px;
  margin-bottom: ${theme.spacing.medium}px;
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.medium}px;
`

const PrimaryAction = styled.div`
  @media screen and (min-width: ${theme.breakpoint.medium}px) {
    & > button {
      min-width: 320px;
    }
  }
`
